import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, AlertCircle, Lightbulb, Target, Zap, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';

const PredictiveInsights = ({ data }) => {
  const insights = [
    {
      icon: TrendingUp,
      title: 'Growth',
      value: data?.predictedGrowth ? `+${data.predictedGrowth}%` : 'N/A',
      description: 'Next 30 days',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      icon: AlertCircle,
      title: 'Risk',
      value: data?.riskScore || 'N/A',
      description: 'Engagement',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      icon: Target,
      title: 'Forecast',
      value: data?.completionForecast ? `${data.completionForecast}%` : 'N/A',
      description: 'Completion',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Zap,
      title: 'Peak Day',
      value: data?.peakDay || 'N/A',
      description: 'Activity',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];

  const recommendations = data?.recommendations || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
      {/* Predictive Metrics */}
      {insights.map((insight, index) => (
        <motion.div
          key={insight.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <Card className="hover:shadow-md transition-all duration-300 border border-gray-200 bg-white">
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-2">
                <div className={`p-2 rounded-lg ${insight.bgColor}`}>
                  <insight.icon className={`w-4 h-4 ${insight.color}`} />
                </div>
                <span className="text-[9px] font-medium text-gray-400 uppercase tracking-wider">{insight.description}</span>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">{insight.title}</p>
                <p className={`text-xl font-bold ${insight.color}`}>{insight.value}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}

      {/* AI Recommendations */}
      <motion.div
        className="lg:col-span-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="border border-amber-200 bg-gradient-to-br from-amber-50/50 via-yellow-50/50 to-orange-50/50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1.5 rounded-lg bg-yellow-100">
                <Lightbulb className="w-4 h-4 text-yellow-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">AI-Powered Recommendations</h3>
              <Sparkles className="w-3.5 h-3.5 text-yellow-500" />
            </div>
            <div className="space-y-2">
              {recommendations.length > 0 ? (
                recommendations.map((rec, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start space-x-2 text-xs p-2.5 rounded-lg bg-white/80 border border-yellow-100"
                  >
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {rec.priority === 'high' ? 'HIGH' : 'MED'}
                    </span>
                    <p className="text-gray-700 flex-1 leading-relaxed">{rec.text}</p>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-3">
                  <p className="text-xs text-gray-500">✨ All systems running optimally. No recommendations at this time.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PredictiveInsights;
